import { Fragment, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { tripsApi, vehiclesApi } from '@/api/endpoints'
import type { Trip, TripList, Vehicle } from '@/api/types'
import { Button, Card, Field, Input, Select } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatCzk, formatDate, formatKm, formatNumber, formatPlate } from '@/utils/format'

/** Kniha jízd: všechny jízdy, volitelně jen za měsíc nebo pro jedno auto. */
export default function TripsPage() {
  const [params, setParams] = useSearchParams()
  const month = params.get('mesic') ?? '' // prázdný = všechny jízdy
  const vehicleId = params.get('auto') ?? ''
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [data, setData] = useState<TripList | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState(false)
  const [copying, setCopying] = useState<{ id: string; date: string } | null>(null)
  const [copyBusy, setCopyBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    vehiclesApi.list().then(setVehicles).catch(() => setVehicles([]))
  }, [])

  useEffect(() => {
    setData(null)
    setError(null)
    setSelected(new Set())
    const m = dayjs(`${month}-01`)
    tripsApi
      .search({
        vehicleId: vehicleId || undefined,
        from: month ? m.format('YYYY-MM-DD') : undefined,
        to: month ? m.endOf('month').format('YYYY-MM-DD') : undefined,
      })
      .then(setData)
      .catch((e) => setError(errorMessage(e, 'Jízdy se nepodařilo načíst.')))
  }, [month, vehicleId, reload])

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const allSelected = !!data && data.trips.length > 0 && data.trips.every((t) => selected.has(t.id))
  const toggleAll = () => setSelected(allSelected || !data ? new Set() : new Set(data.trips.map((t) => t.id)))

  /** Stejná jízda (auto, řidič, účel, trasa) na jiné datum — nová samostatná jízda. */
  const duplicate = async (t: Trip, date: string) => {
    setCopyBusy(true)
    setError(null)
    try {
      await tripsApi.create({
        vehicleId: t.vehicleId,
        tripDate: date,
        driverName: t.driverName,
        purpose: t.purpose,
        note: t.note,
        points: t.points.map((p, i) => ({ place: p.place, km: i === 0 ? null : p.km })),
      })
      setCopying(null)
      setNotice(`Jízda ${t.points.map((p) => p.place).join(' → ')} zkopírována na ${formatDate(date)}.`)
      setReload((r) => r + 1)
    } catch (e) {
      setError(errorMessage(e, 'Jízdu se nepodařilo zkopírovat.'))
    } finally {
      setCopyBusy(false)
    }
  }

  /** Vybrané jízdy, a když není nic zaškrtnuté, celý výpis. */
  const exportPdf = async () => {
    if (!data) return
    const ids = selected.size > 0 ? data.trips.filter((t) => selected.has(t.id)).map((t) => t.id) : data.trips.map((t) => t.id)
    setExporting(true)
    setError(null)
    try {
      const { blob, fileName } = await tripsApi.pdf(ids)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      // Chyba z blob odpovědi má JSON uvnitř blobu — vytáhneme z něj hlášku.
      let message = 'PDF se nepodařilo vytvořit.'
      const body = (e as { response?: { data?: Blob } }).response?.data
      if (body instanceof Blob) {
        try {
          message = JSON.parse(await body.text()).message ?? message
        } catch {
          /* není JSON */
        }
      }
      setError(message)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Kniha jízd</h1>
        <div className="flex items-center gap-2">
          {data && data.trips.length > 0 && (
            <Button variant="secondary" onClick={exportPdf} disabled={exporting}>
              {exporting ? 'Připravuju PDF…' : selected.size > 0 ? `PDF – vybrané (${selected.size})` : 'PDF – celý výpis'}
            </Button>
          )}
          <Link
          to={`/cesty/nova${vehicleId ? `?auto=${vehicleId}` : ''}`}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          Zapsat jízdu
          </Link>
        </div>
      </div>

      <Card className="grid gap-3 sm:grid-cols-[1fr_2fr]">
        <Field label="Měsíc (nepovinné)">
          <div className="flex gap-2">
            <Input type="month" value={month} onChange={(e) => setParam('mesic', e.target.value)} />
            {month && (
              <Button type="button" variant="ghost" onClick={() => setParam('mesic', '')} title="Zrušit filtr měsíce">
                Vše
              </Button>
            )}
          </div>
        </Field>
        <Field label="Auto">
          <Select value={vehicleId} onChange={(e) => setParam('auto', e.target.value)}>
            <option value="">Všechna auta</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {formatPlate(v.plate)} · {v.name}
              </option>
            ))}
          </Select>
        </Field>
      </Card>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {notice && <p className="text-sm text-green-700">{notice}</p>}
      {!data && !error && <p className="text-slate-400">Načítání…</p>}

      {data && data.trips.length === 0 && (
        <Card className="text-center text-sm text-slate-500">{month ? 'V tomhle měsíci žádné jízdy.' : 'Zatím žádné jízdy.'}</Card>
      )}

      {data && data.trips.length > 0 && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-10 py-3 pl-4">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Vybrat všechny jízdy"
                    className="h-4 w-4 accent-brand-600"
                  />
                </th>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3">Trasa a účel</th>
                <th className="px-4 py-3">Auto · řidič</th>
                <th className="px-4 py-3 text-right">Km</th>
                <th className="px-4 py-3 text-right">Tachometr</th>
                <th className="px-4 py-3 text-right">Náhrada</th>
                <th className="w-10 py-3 pr-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.trips.map((t) => (
                <Fragment key={t.id}>
                <tr className={`align-top hover:bg-slate-50 ${selected.has(t.id) ? 'bg-brand-50' : ''}`}>
                  <td className="py-3 pl-4">
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggle(t.id)}
                      aria-label={`Vybrat jízdu ${formatDate(t.tripDate)}`}
                      className="h-4 w-4 accent-brand-600"
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <Link to={`/cesty/${t.id}`} className="font-medium text-brand-700 hover:underline">
                      {formatDate(t.tripDate)}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div>{t.points.map((p) => p.place).join(' → ')}</div>
                    <div className="text-xs text-slate-500">{t.purpose}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="whitespace-nowrap font-mono text-xs">{formatPlate(t.vehiclePlate)}</div>
                    <div className="text-xs text-slate-500">{t.driverName}</div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium">{formatKm(t.totalKm)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-xs text-slate-500">
                    {t.odometerStart !== null ? (
                      <>
                        {t.odometerStart.toLocaleString('cs-CZ')} → {t.odometerEnd!.toLocaleString('cs-CZ')}
                        {t.odometerManual && <div className="text-amber-700">start ručně</div>}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    {formatCzk(t.amount)}
                    {t.ownership === 'COMPANY' && t.amount !== null && (
                      <div className="text-xs text-slate-400">jen PHM</div>
                    )}
                    {t.rate.fuelPrice && (
                      <div
                        className={`text-xs ${t.fuelPrice !== null ? 'text-emerald-700' : 'text-slate-400'}`}
                        title={t.fuelPrice !== null ? 'Cena PHM z dokladu o tankování' : `Průměrná cena PHM (${t.rate.fuelPrice.source})`}
                      >
                        PHM {formatNumber(t.rate.fuelPrice.value)} Kč/{t.fuelUnit}
                        {t.fuelPrice !== null && ' · doklad'}
                        {t.files.length > 0 && (
                          <a
                            href={tripsApi.fileUrl(t.files[0].id, true)}
                            target="_blank"
                            rel="noreferrer"
                            title={t.files.map((f) => f.fileName).join(', ')}
                            aria-label={`Doklad k jízdě ${formatDate(t.tripDate)}`}
                            className="ml-1 hover:underline"
                          >
                            📎{t.files.length > 1 ? t.files.length : ''}
                          </a>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        setCopying(
                          copying?.id === t.id ? null : { id: t.id, date: dayjs(t.tripDate).add(1, 'day').format('YYYY-MM-DD') },
                        )
                      }
                      title="Duplikovat na jiné datum"
                      aria-label={`Duplikovat jízdu ${formatDate(t.tripDate)}`}
                      aria-expanded={copying?.id === t.id}
                      className="inline-block rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-600"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                  </td>
                </tr>
                {copying?.id === t.id && (
                  <tr className="bg-slate-50">
                    <td colSpan={9} className="px-4 py-3">
                      <form
                        className="flex flex-wrap items-end justify-end gap-2"
                        onSubmit={(e) => {
                          e.preventDefault()
                          duplicate(t, copying.date)
                        }}
                        onKeyDown={(e) => e.key === 'Escape' && setCopying(null)}
                      >
                        <span className="mr-auto self-center text-sm text-slate-600">
                          Duplikovat {t.points.map((p) => p.place).join(' → ')} ({formatKm(t.totalKm)}) na:
                        </span>
                        <div className="w-44">
                          <Input
                            type="date"
                            required
                            autoFocus
                            aria-label="Datum kopie"
                            value={copying.date}
                            onChange={(e) => setCopying({ id: t.id, date: e.target.value })}
                          />
                        </div>
                        <Button type="submit" disabled={copyBusy || !copying.date}>
                          {copyBusy ? 'Ukládám…' : 'Uložit kopii'}
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => setCopying(null)}>
                          Zrušit
                        </Button>
                      </form>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
            <tfoot className="border-t border-slate-200 font-semibold">
              <tr>
                <td className="px-4 py-3" colSpan={4}>
                  Celkem {data.trips.length} {data.trips.length === 1 ? 'jízda' : data.trips.length < 5 ? 'jízdy' : 'jízd'}
                  {data.withoutAmount > 0 && (
                    <span className="ml-2 text-xs font-normal text-amber-700">
                      {data.withoutAmount} bez náhrady (chybí spotřeba nebo cena PHM)
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">{formatKm(data.totalKm)}</td>
                <td />
                <td className="whitespace-nowrap px-4 py-3 text-right">{formatCzk(data.totalAmount)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </Card>
      )}
    </div>
  )
}
